import React, { useRef, useState } from 'react';
import {
  List,
  Show,
  Edit,
  Create,
  SimpleForm,
  TextInput,
  NumberInput,
  BooleanInput,
  useShowContext,
  useDataProvider,
  useRedirect,
  useGetList,
  useInput,
  useRecordContext,
} from 'react-admin';
import { useBreadcrumbContext } from '../components/layout/AppBreadcrumb';
import { Trophy, Trash2, Plus, Pencil, Upload, ImageOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { EnvironmentService } from '../services/EnvironmentService';
import { TokenManager } from '../utils/TokenManager';
import { SearchIntegration } from '../components/common/SearchIntegration';
import { ReactAdminPagination } from '../components/ui/react-admin-pagination';
import { ListScreen } from '../components/ui/list-screen';
import { StatCard, StatRow } from '../components/ui/stat-card';
import { DataTable, DataColumn } from '../components/ui/data-table';
import { SortMenu } from '../components/ui/sort-menu';
import { FilterTokens } from '../components/ui/filter-controls';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import { Card, CardContent } from '../components/ui/card';
import { StatusBadge, hueFromString } from '../components/ui/data-cells';
import { toastSuccess, toastError } from '../lib/toast';
import { cn } from '../lib/utils';
import { CustomDataProvider } from '../types/dataProvider';

/* ============================================================
   Sports — Latio design system (derived). List + full-page detail + CRUD form.
   ============================================================ */

interface Sport {
  id: number;
  key: string;
  name_en?: string;
  name_es?: string;
  name_pt?: string;
  category?: string;
  display_order?: number;
  is_active?: boolean;
  requires_certificate?: boolean;
  icon_url?: string;
  description_en?: string;
  description_es?: string;
  description_pt?: string;
  created_at?: string;
}

const validateSportKey = (value: string) => {
  if (!value) return 'Required';
  if (!/^[a-z0-9_-]+$/.test(value)) return 'Lowercase letters, numbers, hyphens and underscores only';
  return undefined;
};

const fmtDate = (v?: string | null) =>
  v ? new Date(v).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }) : '—';

/* ---- Sport icon tile ---- */
function SportTile({ sport, size = 40 }: { sport: Sport; size?: number }) {
  const hue = hueFromString(sport.name_en || sport.key);
  if (sport.icon_url) {
    return <img src={sport.icon_url} alt="" width={size} height={size} className="rounded-latio-sm object-cover" style={{ width: size, height: size }} />;
  }
  return (
    <span
      className="flex shrink-0 items-center justify-center rounded-latio-sm"
      style={{ width: size, height: size, backgroundColor: `hsl(${hue} 60% 92%)`, color: `hsl(${hue} 55% 38%)` }}
      aria-hidden
    >
      <Trophy className="size-1/2" />
    </span>
  );
}

/* ---- List ---- */
const SPORT_SORT = [
  { field: 'display_order', label: 'Order' },
  { field: 'name_en', label: 'Name' },
  { field: 'category', label: 'Category' },
  { field: 'id', label: 'ID' },
];
const SPORT_SORT_LABELS = Object.fromEntries(SPORT_SORT.map((f) => [f.field, f.label]));

const SportStats = () => {
  const { data } = useGetList<Sport>('sports', { pagination: { page: 1, perPage: 200 }, sort: { field: 'display_order', order: 'ASC' }, filter: {} });
  const rows = data || [];
  const active = rows.filter((s) => s.is_active).length;
  const categories = new Set(rows.map((s) => s.category).filter(Boolean)).size;
  const v = (n: number) => (rows.length === 0 ? '—' : n);
  return (
    <StatRow>
      <StatCard primary label="Total sports" value={rows.length || '—'} />
      <StatCard label="Active" value={v(active)} />
      <StatCard label="Inactive" value={v(rows.length - active)} />
      <StatCard label="Categories" value={v(categories)} />
    </StatRow>
  );
};

const NewSportButton = () => {
  const redirect = useRedirect();
  return (
    <button
      type="button"
      onClick={() => redirect('create', 'sports')}
      className="inline-flex h-9 items-center gap-2 rounded-pill bg-black px-4 text-sm font-semibold text-white hover:bg-ink"
    >
      <Plus className="size-4" />
      New sport
    </button>
  );
};

const SportsListBody = () => {
  const columns: DataColumn<Sport>[] = [
    { key: 'id', header: 'ID', sortable: true, width: '1px', cell: (r) => <span className="tabular-nums text-ink-2">#{r.id}</span> },
    {
      key: 'name_en',
      header: 'Sport',
      sortable: true,
      cell: (r) => (
        <div className="flex items-center gap-3">
          <SportTile sport={r} />
          <div className="min-w-0">
            <div className="truncate font-semibold text-ink">{r.name_en || r.key}</div>
            <div className="truncate text-[13px] text-ink-3"><code>{r.key}</code></div>
          </div>
        </div>
      ),
    },
    { key: 'category', header: 'Category', sortable: true, cell: (r) => <span className="capitalize text-ink-2">{r.category || '—'}</span> },
    { key: 'display_order', header: 'Order', sortable: true, align: 'right', cell: (r) => <span className="tabular-nums text-ink-2">{r.display_order ?? '—'}</span> },
    { key: 'is_active', header: 'Status', cell: (r) => <StatusBadge status={r.is_active ? 'active' : 'draft'} /> },
    { key: 'created_at', header: 'Created', cell: (r) => fmtDate(r.created_at) },
  ];
  return (
    <ListScreen
      stats={<SportStats />}
      actions={
        <>
          <SortMenu fields={SPORT_SORT} />
          <NewSportButton />
        </>
      }
      tokens={<FilterTokens fields={[]} sortLabels={SPORT_SORT_LABELS} />}
    >
      <DataTable columns={columns} rowClick="show" emptyMessage="No sports found." />
      <div className="pt-1">
        <ReactAdminPagination />
      </div>
    </ListScreen>
  );
};

export const SportList = (props: any) => (
  <List {...props} actions={false} empty={false} pagination={false} sort={{ field: 'display_order', order: 'ASC' }} perPage={50} component="div">
    <SearchIntegration filterKey="q" />
    <SportsListBody />
  </List>
);

/* ---- Detail ---- */
function DetailCard({ title, children, className }: { title?: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={cn('rounded-card border border-hairline bg-background p-5', className)}>
      {title && <h3 className="mb-3 text-[15px] font-bold text-ink">{title}</h3>}
      {children}
    </div>
  );
}
function Fact({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <span className="text-[13px] text-ink-2">{label}</span>
      <span className="text-right text-sm font-medium text-ink">{value}</span>
    </div>
  );
}
function I18nRow({ label, value }: { label: string; value?: string }) {
  return (
    <div className="py-1.5">
      <div className="text-[11.5px] font-semibold uppercase tracking-[0.06em] text-ink-3">{label}</div>
      <p className="mt-0.5 text-sm text-ink">{value || '—'}</p>
    </div>
  );
}

const SportShowContent = () => {
  const { record, isLoading } = useShowContext<Sport>();
  const dataProvider = useDataProvider<CustomDataProvider>();
  const redirect = useRedirect();
  const { setData } = useBreadcrumbContext();
  const [removeOpen, setRemoveOpen] = React.useState(false);
  const [sessionsCount, setSessionsCount] = React.useState<number | null>(null);

  React.useEffect(() => {
    if (record) setData({ resourceId: record.id.toString(), resourceName: record.name_en || record.key, resourceType: 'session' });
  }, [record, setData]);

  React.useEffect(() => {
    if (!record || !removeOpen) return;
    (async () => {
      try {
        const { data } = await dataProvider.getSportsStatsOverview();
        const row = (data || []).find((s: any) => s.sport_id === record.id);
        setSessionsCount(row?.total_sessions ?? 0);
      } catch {
        setSessionsCount(null);
      }
    })();
  }, [record, removeOpen, dataProvider]);

  if (isLoading) return <div className="p-6 text-center text-ink-2">Loading sport…</div>;
  if (!record) return <div className="p-6 text-center text-ink-2">Sport not found</div>;

  const remove = async () => {
    try {
      await dataProvider.delete('sports', { id: record.id, previousData: record });
      toastSuccess('Sport deleted');
      redirect('list', 'sports');
    } catch (e: any) {
      toastError(e?.message || 'Failed to delete sport');
    }
  };

  const hasSessions = (sessionsCount ?? 0) > 0;

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <SportTile sport={record} size={60} />
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-[24px] font-bold leading-none text-ink" style={{ letterSpacing: '-0.02em' }}>
                {record.name_en || record.key}
              </h1>
              <StatusBadge status={record.is_active ? 'active' : 'draft'} />
            </div>
            <p className="mt-1 text-sm text-ink-2"><code>{record.key}</code> · Sport #{record.id}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => redirect('edit', 'sports', record.id)}
            className="inline-flex h-9 items-center gap-2 rounded-pill border border-hairline px-4 text-sm font-semibold text-ink hover:bg-surface-2"
          >
            <Pencil className="size-4" />
            Edit
          </button>
          <button
            onClick={() => setRemoveOpen(true)}
            className="inline-flex h-9 items-center gap-2 rounded-pill bg-error px-4 text-sm font-semibold text-white hover:bg-error/90"
          >
            <Trash2 className="size-4" />
            Delete
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <DetailCard title="Details">
          <div className="divide-y divide-hairline">
            <Fact label="Category" value={<span className="capitalize">{record.category || 'Not set'}</span>} />
            <Fact label="Display order" value={record.display_order ?? '—'} />
            <Fact label="Status" value={<StatusBadge status={record.is_active ? 'active' : 'draft'} />} />
            <Fact label="Requires certificate" value={record.requires_certificate ? 'Yes' : 'No'} />
            <Fact label="Created" value={fmtDate(record.created_at)} />
          </div>
        </DetailCard>

        <DetailCard title="Names">
          <div className="divide-y divide-hairline">
            <I18nRow label="English" value={record.name_en} />
            <I18nRow label="Spanish" value={record.name_es} />
            <I18nRow label="Portuguese" value={record.name_pt} />
          </div>
        </DetailCard>

        <DetailCard title="Descriptions" className="lg:col-span-2">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <I18nRow label="English" value={record.description_en} />
            <I18nRow label="Spanish" value={record.description_es} />
            <I18nRow label="Portuguese" value={record.description_pt} />
          </div>
        </DetailCard>
      </div>

      <ConfirmDialog
        open={removeOpen}
        onOpenChange={setRemoveOpen}
        tone="destructive"
        title={`Delete "${record.name_en || record.key}"?`}
        consequence={
          hasSessions
            ? `This sport is referenced by ${sessionsCount} session(s); deleting it may leave them inconsistent. This cannot be undone.`
            : 'This permanently deletes the sport. This cannot be undone.'
        }
        confirmLabel="Delete sport"
        onConfirm={remove}
      />
    </div>
  );
};

export const SportShow = (props: any) => (
  <Show {...props} actions={false} title=" " component="div">
    <SportShowContent />
  </Show>
);

/* ---- Icon upload (edit mode only — needs an existing sport id) ---- */
const SportIconUpload: React.FC = () => {
  const record = useRecordContext<Sport>();
  const { field } = useInput({ source: 'icon_url' });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !record?.id) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = TokenManager.getAccessToken();
      const response = await fetch(
        `${EnvironmentService.getApiUrl()}/admin/sports/${record.id}/icon`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        }
      );
      const json = await response.json();
      if (!response.ok) {
        throw new Error(json?.detail || json?.message || 'Upload failed');
      }
      field.onChange(json.data.icon_url);
      toastSuccess('Icon uploaded');
    } catch (err: any) {
      toastError(err?.message || 'Failed to upload icon');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-ink">Icon</label>
      <div className="flex items-center gap-4">
        <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-hairline bg-surface-2">
          {field.value ? (
            <img src={field.value} alt="Sport icon" className="h-full w-full object-contain" />
          ) : (
            <ImageOff className="h-6 w-6 text-ink-3" />
          )}
        </div>
        <div className="space-y-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="h-4 w-4" />
            {uploading ? 'Uploading…' : field.value ? 'Replace icon' : 'Upload icon'}
          </Button>
          <p className="text-xs text-ink-2">PNG, JPEG or WebP, up to 2MB.</p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={handleFileSelect}
        />
      </div>
    </div>
  );
};

/* ---- Edit / Create (RA form) ---- */
const SportForm: React.FC<{ mode: 'create' | 'edit' }> = ({ mode }) => (
  <SimpleForm>
    <Card>
      <CardContent className="space-y-4 p-6">
        <h2 className="mb-1 text-[17px] font-bold text-ink">{mode === 'create' ? 'Create sport' : 'Edit sport'}</h2>
        <TextInput
          source="key"
          label="Key (slug)"
          required
          validate={validateSportKey}
          helperText="Unique identifier, e.g. 'football', 'tennis'"
          disabled={mode === 'edit'}
          fullWidth
        />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <TextInput source="name_en" label="Name (EN)" required fullWidth />
          <TextInput source="name_es" label="Name (ES)" fullWidth />
          <TextInput source="name_pt" label="Name (PT)" fullWidth />
        </div>
        <TextInput source="category" helperText="e.g. team, individual, racket" fullWidth />
        {mode === 'edit' ? (
          <SportIconUpload />
        ) : (
          <TextInput
            source="icon_url"
            label="Icon URL"
            helperText="You can also upload an image after creating the sport"
            fullWidth
          />
        )}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <TextInput source="description_en" label="Description (EN)" multiline rows={3} fullWidth />
          <TextInput source="description_es" label="Description (ES)" multiline rows={3} fullWidth />
          <TextInput source="description_pt" label="Description (PT)" multiline rows={3} fullWidth />
        </div>
        <div className="flex items-center gap-4">
          <NumberInput source="display_order" defaultValue={0} />
          <BooleanInput source="is_active" defaultValue={true} />
        </div>
        <BooleanInput
          source="requires_certificate"
          label="Requires certificate"
          defaultValue={false}
          helperText="Trainers must have an admin-approved certificate before holding this specialty"
        />
      </CardContent>
    </Card>
  </SimpleForm>
);

export const SportEdit = (props: any) => (
  <Edit {...props} mutationMode="pessimistic">
    <SportForm mode="edit" />
  </Edit>
);

export const SportCreate = (props: any) => (
  <Create {...props}>
    <SportForm mode="create" />
  </Create>
);
